/**
 * Settings page for user account management.
 */
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useNavigate } from 'react-router-dom';
import { useSettings } from '../hooks/useSettings';
import { useAuth } from '../../auth/context/AuthContext';
import ProfilePictureUpload from '../components/ProfilePictureUpload';
import PasswordChangeForm from '../components/PasswordChangeForm';
import { PrivacySettings } from '../components/PrivacySettings';
import ConfirmationModal from '../../../shared/components/ui/ConfirmationModal';
import { BASE_URL } from '../../../shared/lib/api';

const profileSchema = z.object({
  first_name: z.string().min(1, 'First name is required'),
  last_name: z.string().min(1, 'Last name is required'),
});

type ProfileFormData = z.infer<typeof profileSchema>;

const SettingsPage: React.FC = () => {
  const { profile, loading, error, updateProfile, refetch } = useSettings();
  const { session, logout } = useAuth();
  const navigate = useNavigate();
  const [success, setSuccess] = useState(false);
  const [updateError, setUpdateError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDeletingPortfolio, setIsDeletingPortfolio] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<ProfileFormData>({
    resolver: zodResolver(profileSchema),
    defaultValues: {
      first_name: profile?.first_name || '',
      last_name: profile?.last_name || '',
    },
  });

  // Update form when profile loads
  React.useEffect(() => {
    if (profile) {
      reset({
        first_name: profile.first_name || '',
        last_name: profile.last_name || '',
      });
    }
  }, [profile, reset]);

  const onSubmit = async (data: ProfileFormData) => {
    try {
      setIsSubmitting(true);
      setUpdateError(null);
      setSuccess(false);

      await updateProfile(data);
      setSuccess(true);
      
      // Clear success message after 5 seconds
      setTimeout(() => setSuccess(false), 5000);
    } catch (err) {
      setUpdateError(err instanceof Error ? err.message : 'Failed to update profile');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeletePortfolioClick = () => {
    setIsDeleteModalOpen(true);
  };

  const handleCancelDeletePortfolio = () => {
    if (isDeletingPortfolio) return; // Prevent closing during deletion
    setIsDeleteModalOpen(false);
  };

  const handleConfirmDeletePortfolio = async () => {
    if (!session?.access_token) {
      setUpdateError('No active session. Please log in again.');
      setIsDeleteModalOpen(false);
      return;
    }

    setIsDeletingPortfolio(true);

    try {
      const response = await fetch(`${BASE_URL}/settings/portfolio`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Failed to delete portfolio' }));
        throw new Error(errorData.error || 'Failed to delete portfolio');
      }

      // Portfolio deleted successfully
      // Sign out and redirect to homepage
      await logout();
      navigate('/');
    } catch (err) {
      console.error('Error deleting portfolio:', err);
      setUpdateError(err instanceof Error ? err.message : 'Failed to delete portfolio');
      setIsDeletingPortfolio(false);
      // Keep modal open on error so user can see the message
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-red-600">{error}</div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-gray-600">Profile not found</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Settings</h1>
          <p className="mt-2 text-sm text-gray-600">
            Manage your account settings and preferences
          </p>
        </div>

        <div className="space-y-6">
          {/* Profile Section */}
          <div className="bg-white shadow rounded-lg p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-6">Profile</h2>

            {/* Profile Picture */}
            <div className="mb-8">
              <label className="block text-sm font-medium text-gray-700 mb-4">
                Profile Picture
              </label>
              <ProfilePictureUpload profile={profile} onUpdate={refetch} />
            </div>

            {/* Profile Form */}
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
              {success && (
                <div className="rounded-md bg-green-50 p-4">
                  <div className="text-sm text-green-700">Profile updated successfully!</div>
                </div>
              )}

              {/* Username (read-only) */}
              <div>
                <label htmlFor="username" className="block text-sm font-medium text-gray-700">
                  Username
                </label>
                <div className="mt-1">
                  <input
                    id="username"
                    type="text"
                    value={profile.username || ''}
                    disabled
                    className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-gray-50 text-gray-500 cursor-not-allowed sm:text-sm"
                  />
                  <p className="mt-2 text-xs text-gray-500">
                    Username cannot be changed
                  </p>
                </div>
              </div>

              {/* Email (read-only) */}
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                  Email
                </label>
                <div className="mt-1">
                  <input
                    id="email"
                    type="email"
                    value={profile.email || ''}
                    disabled
                    className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-gray-50 text-gray-500 cursor-not-allowed sm:text-sm"
                  />
                  <p className="mt-2 text-xs text-gray-500">
                    Email cannot be changed
                  </p>
                </div>
              </div>

              {/* First Name (editable) */}
              <div>
                <label htmlFor="first_name" className="block text-sm font-medium text-gray-700">
                  First Name
                </label>
                <div className="mt-1">
                  <input
                    id="first_name"
                    type="text"
                    autoComplete="given-name"
                    className={`appearance-none block w-full px-3 py-2 border ${
                      errors.first_name ? 'border-red-300' : 'border-gray-300'
                    } rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm`}
                    {...register('first_name')}
                  />
                  {errors.first_name && (
                    <p className="mt-2 text-sm text-red-600">{errors.first_name.message}</p>
                  )}
                </div>
              </div>

              {/* Last Name (editable) */}
              <div>
                <label htmlFor="last_name" className="block text-sm font-medium text-gray-700">
                  Last Name
                </label>
                <div className="mt-1">
                  <input
                    id="last_name"
                    type="text"
                    autoComplete="family-name"
                    className={`appearance-none block w-full px-3 py-2 border ${
                      errors.last_name || updateError ? 'border-red-300' : 'border-gray-300'
                    } rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm`}
                    {...register('last_name')}
                  />
                  {errors.last_name && (
                    <p className="mt-2 text-sm text-red-600">{errors.last_name.message}</p>
                  )}
                  {updateError && !errors.last_name && (
                    <p className="mt-2 text-sm text-red-600">{updateError}</p>
                  )}
                </div>
              </div>

              <div>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full sm:w-auto px-6 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>

          {/* Password Section */}
          <div className="bg-white shadow rounded-lg p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-6">Password</h2>
            <PasswordChangeForm />
          </div>

          {/* Notifications Section (Placeholder) */}
          <div className="bg-white shadow rounded-lg p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-6">Notifications</h2>
            <p className="text-sm text-gray-500">
              Notification preferences will be available in a future update.
            </p>
          </div>

          {/* Privacy Section */}
          <div className="bg-white shadow rounded-lg p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-6">Privacy</h2>
            <PrivacySettings
              profile={profile}
              onUpdate={async (data) => {
                await updateProfile(data);
              }}
            />
          </div>

          {/* Delete Portfolio Section */}
          <div className="bg-white shadow rounded-lg p-6 border-l-4 border-red-500">
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Delete Portfolio</h2>
            <p className="text-sm text-gray-600 mb-4">
              Permanently delete your portfolio and all associated data. This action cannot be undone.
            </p>
            {updateError && (
              <div className="mb-4 rounded-md bg-red-50 p-4">
                <div className="text-sm text-red-700">{updateError}</div>
              </div>
            )}
            <button
              type="button"
              onClick={handleDeletePortfolioClick}
              disabled={isDeletingPortfolio}
              className="px-4 py-2 bg-red-600 text-white rounded-md shadow-sm text-sm font-medium hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Delete Portfolio
            </button>
          </div>
        </div>
      </div>

      {/* Delete Portfolio Confirmation Modal */}
      <ConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={handleCancelDeletePortfolio}
        title="Delete Portfolio"
        message={
          <>
            <p className="mb-2">
              Are you sure you want to permanently delete your portfolio?
            </p>
            <p className="text-red-600 font-medium">
              This will permanently delete all of your data, including:
            </p>
            <ul className="list-disc list-inside mt-2 text-sm text-gray-700 space-y-1">
              <li>All accounts and transactions</li>
              <li>All portfolio data and history</li>
              <li>All friendships and friend requests</li>
            </ul>
            <p className="mt-3 text-sm text-gray-600">
              Note: Your name and profile picture will remain visible in past conversations, but your portfolio will be permanently deleted.
            </p>
            <p className="mt-4 font-medium text-red-600">
              This action cannot be undone.
            </p>
          </>
        }
        confirmLabel="Yes, delete my portfolio"
        cancelLabel="Cancel"
        onConfirm={handleConfirmDeletePortfolio}
        onCancel={handleCancelDeletePortfolio}
        isLoading={isDeletingPortfolio}
        variant="danger"
      />
    </div>
  );
};

export default SettingsPage;

